import { useEffect, useRef, useState } from "react";

import { useHandleMagicLinkSignIn } from "~/auth/hooks/use-handle-magic-link-sign-in";
import { useHandleMagicLinkSignUp } from "~/auth/hooks/use-handle-magic-link-sign-up";
import { useT } from "~/i18n/use-t";
import { cn } from "~/util/cn";

export const ButtonResend: React.FC<{
  mode: "signIn" | "signUp";
  countdownMs: number;
  data: { name: string; email: string };
  className?: string;
}> = ({ mode, countdownMs, data: { email, name }, className }) => {
  const t = useT();
  const [isLoading, setIsLoading] = useState(false);
  const [canRequestResend, setCanRequestResend] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(countdownMs / 1000));
  const handleMagicLinkSignUp = useHandleMagicLinkSignUp({ legacy: false });
  const handleMagicLinkSignIn = useHandleMagicLinkSignIn();

  const intervalId = useRef<NodeJS.Timeout | null>(null);

  const resendText = t(
    canRequestResend
      ? { tx: "graph.choice.SIGNUP.resend" }
      : {
          tx: "graph.choice.SIGNUP.resendIn",
          txData: { time: secondsLeft },
        },
  );

  useEffect(() => {
    setCanRequestResend(false);
    setSecondsLeft(Math.ceil(countdownMs / 1000));

    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    intervalId.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          setCanRequestResend(true);
          if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [countdownMs]);

  return (
    <button
      title={resendText}
      disabled={isLoading || !canRequestResend}
      className={cn("btn", className)}
      onClick={() => {
        setIsLoading(true);
        if (mode === "signIn") {
          handleMagicLinkSignIn({ email, name });
        } else {
          handleMagicLinkSignUp({ email, name });
        }

        setSecondsLeft(Math.ceil(countdownMs / 1000));
        setCanRequestResend(false);

        if (intervalId.current) {
          clearInterval(intervalId.current);
        }

        intervalId.current = setInterval(() => {
          setSecondsLeft((prev) => {
            if (prev <= 1) {
              setCanRequestResend(true);
              if (intervalId.current) {
                clearInterval(intervalId.current);
                intervalId.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        setIsLoading(false);
      }}
    >
      {resendText}
    </button>
  );
};
